import { useEffect, useState } from "react";
import { MapPin } from "lucide-react";
import { api, ApiError } from "@main/api/client";
import { LoadingBlock } from "@main/components/Spinner";
import { sectorLabel } from "@main/data/sectorsData";
import { theme, card, btnPrimary, inputStyle, pageContainer, pageHeading, pageSubtitle } from "@main/theme";

// ── My Organisation (Jersey edition) ──────────────────────────────────────
// The profile fields a Jersey organisation keeps. Operating location is fixed
// to Jersey by the edition (the API refuses any other country), and there is
// no data protection tier or DPO: data protection is not part of the edition.
// The regulated sector — what the Cyber Security (Jersey) Law designation
// reads — is set through the sector prompt and shown here.

const FIELDS = [
  { key: "name", label: "Organisation name" },
  { key: "sector", label: "What the organisation does" },
  { key: "ceo", label: "Director / owner" },
  { key: "ciso", label: "Security lead" },
  { key: "itManager", label: "IT manager or provider" },
  { key: "infrastructure", label: "IT infrastructure (in a sentence)" },
  { key: "cloudProviders", label: "Cloud services used" },
  { key: "coreApps", label: "Core business applications" },
  { key: "backup", label: "How data is backed up" },
] as const;

type FieldKey = (typeof FIELDS)[number]["key"] | "size";
type Profile = Partial<Record<FieldKey, string | null>> & { sectorCode?: string | null };

const SIZES = ["1–10", "11–50", "51–200", "201–1,000", "1,000+"];

export function JsyOrgProfilePage() {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    api.get<{ organization: Profile }>("/organizations/profile")
      .then((d) => setProfile(d.organization))
      .catch(() => setMessage({ ok: false, text: "Could not load your organisation." }));
  }, []);

  const set = (key: FieldKey, value: string) => setProfile((p) => ({ ...(p ?? {}), [key]: value }));

  const save = async () => {
    if (!profile) return;
    setSaving(true);
    setMessage(null);
    try {
      const body: Record<string, string | null> = {};
      for (const f of FIELDS) body[f.key] = (profile[f.key] ?? "").trim() || null;
      body.size = profile.size || null;
      if (!body.name) throw new Error("The organisation needs a name.");
      await api.patch("/organizations/profile", body);
      setMessage({ ok: true, text: "Saved." });
    } catch (e) {
      setMessage({ ok: false, text: e instanceof ApiError || e instanceof Error ? e.message : "Could not save." });
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return <div style={pageContainer}>{message ? <div style={{ color: theme.color.dangerText }}>{message.text}</div> : <LoadingBlock />}</div>;

  return (
    <div style={pageContainer}>
      <h1 style={pageHeading}>My Organisation</h1>
      <p style={pageSubtitle}>What the platform, your consultant and the AI checks know about you.</p>

      <div style={{ ...card, marginBottom: 16, display: "flex", gap: 24, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.subtle, textTransform: "uppercase", letterSpacing: "0.06em" }}>Operating location</div>
          <div style={{ fontSize: 14, color: theme.color.heading, marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
            <MapPin size={14} color="var(--c-accent)" /> Jersey
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.subtle, textTransform: "uppercase", letterSpacing: "0.06em" }}>Regulated sector</div>
          <div style={{ fontSize: 14, color: theme.color.heading, marginTop: 4 }}>
            {profile.sectorCode ? sectorLabel(profile.sectorCode) ?? profile.sectorCode : "Not yet chosen"}
          </div>
        </div>
      </div>

      <div style={card}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label htmlFor={`jsy-${f.key}`} style={{ display: "block", fontSize: 12, fontWeight: 600, color: theme.color.muted, marginBottom: 6 }}>{f.label}</label>
              <input id={`jsy-${f.key}`} style={inputStyle} value={profile[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} />
            </div>
          ))}
          <div>
            <label htmlFor="jsy-size" style={{ display: "block", fontSize: 12, fontWeight: 600, color: theme.color.muted, marginBottom: 6 }}>Approximate headcount</label>
            <select id="jsy-size" style={inputStyle} value={profile.size ?? ""} onChange={(e) => set("size", e.target.value)}>
              <option value="">Select…</option>
              {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 18 }}>
          <button type="button" onClick={save} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>{saving ? "Saving…" : "Save"}</button>
          {message && <span role="status" style={{ fontSize: 12, color: message.ok ? theme.color.successText : theme.color.dangerText }}>{message.text}</span>}
        </div>
      </div>
    </div>
  );
}
